/* LandingPageDark — CountBucks "All-in-One Business Hub with AI"
 *
 * Dark AI-native design (archetype: Linear / Vercel / Anthropic / OpenRouter).
 * Sections in order:
 *   1. Sticky glass nav
 *   2. Hero (overline + H1 + sub + CTA + <TypewriterDemo/>)
 *   3. Trust strip (AI provider wordmarks)
 *   4. Replaces receipt ($144/mo → $19.99)
 *   5. Feature bento (6 pillars)
 *   6. AI Hub teaser
 *   7. Audience cards (CPA / SMB / Household / Freelancer)
 *   8. Pricing strip (4 paid tiers)
 *   9. Footer CTA
 *
 * Auth modal trigger routes the user to /login (slim Phase-3 page) when it
 * exists, falling back to the existing dialog-based flow on the legacy
 * LandingPage.js until then.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import TypewriterDemo from '../components/landing/TypewriterDemo';
// PromoVideoEmbed (components/PromoVideoEmbed.jsx) is archived — removed from
// the hero per Eli's request Jun 2026; re-import and render to restore.
import BackToTop from '../components/landing/BackToTop';
import LandingNav from '../components/landing/LandingNav';
import { trackFunnel } from '../lib/funnelTracker';
import {
  AnthropicMark, OpenAIMark, GeminiMark, MetaMark, PerplexityMark,
} from '../components/landing/BrandLogos';
import {
  ArrowRight, Sparkles, FileText, Landmark, Brain, Clock, Users, Scale,
  Check, ShieldCheck, Zap, Infinity as InfinityIcon, X,
  Building2, Home, Briefcase, Wallet,
  BookOpen, CreditCard, PenTool, Calculator, Bot, ChevronDown,
} from 'lucide-react';
import { cn } from '../lib/utils';

const REPLACED_SUBS = [
  { name: 'QuickBooks Online', cost: 30, category: 'Bookkeeping' },
  { name: 'Dropbox Plus', cost: 12, category: 'Doc Storage' },
  { name: 'ChatGPT Plus', cost: 20, category: 'AI · GPT-4o' },
  { name: 'Claude Pro', cost: 20, category: 'AI · Anthropic' },
  { name: 'Gemini Advanced', cost: 20, category: 'AI · Google' },
  { name: 'Perplexity Pro', cost: 20, category: 'AI · Web Search' },
  { name: 'Harvest / Toggl', cost: 12, category: 'Time Tracking' },
  { name: 'Receipt Bank / Hubdoc', cost: 10, category: 'OCR & Scanning' },
];
const REPLACED_TOTAL = REPLACED_SUBS.reduce((s, x) => s + x.cost, 0);

const TRUST_LOGOS = [
  { name: 'Anthropic', sub: 'Claude', Mark: AnthropicMark },
  { name: 'OpenAI', sub: 'GPT-4o · GPT-5', Mark: OpenAIMark },
  { name: 'Google', sub: 'Gemini', Mark: GeminiMark },
  { name: 'Meta', sub: 'Llama 3.3', Mark: MetaMark },
  { name: 'Perplexity', sub: 'Sonar', Mark: PerplexityMark },
];

const FEATURES = [
  { icon: FileText, title: 'Document Hub', tag: 'Smart folders · OCR · 7-yr retention',
    blurb: 'Dropbox + Receipt Bank + folder automations in one place. AI auto-categorizes every upload.', span: 'md:col-span-2' },
  { icon: Landmark, title: 'Banking', tag: 'Plaid · auto-reconcile',
    blurb: 'Connect your bank in 30 seconds. AI matches every transaction to the right account.', span: '' },
  { icon: Brain, title: 'AI Assistant', tag: '240+ models · one chat box',
    blurb: 'Claude, GPT-4o, Gemini, Perplexity and Llama — all under one roof. Auto-picks the right model for the job and falls back to free Llama if your cap runs out.',
    span: 'md:col-span-3', wide: true },
  { icon: Clock, title: 'Time + Invoicing', tag: 'Tracker · auto-bills',
    blurb: 'Turn billable hours into Stripe invoices in two clicks.', span: '' },
  { icon: Users, title: 'Client Portal', tag: 'Secure · branded',
    blurb: 'Send docs, collect payments, request signatures.', span: '' },
  { icon: Scale, title: 'Reconciliation', tag: 'Books match bank, every month',
    blurb: 'AI flags duplicates, missing entries, and currency drift before you ever see them.', span: '' },
];

const AUDIENCES = [
  { icon: Building2, title: 'CPA Firms', blurb: '10 → 100 client books in one workspace.' },
  { icon: Briefcase, title: 'Small Business', blurb: 'Books, payroll prep, invoicing, AI — done.' },
  { icon: Home, title: 'Households', blurb: 'Budget, receipts, taxes — like Mint with brains.' },
  { icon: Wallet, title: 'Freelancers', blurb: 'Track time, bill clients, file taxes — solo.' },
];

// ─── Full firm capability menu (expandable) ─────────────────────────────────
const FIRM_CATEGORIES = [
  {
    id: 'bookkeeping', Icon: BookOpen, accent: 'brand',
    title: 'Bookkeeping & GL',
    tag: 'Books · reconciliation · audit trail',
    blurb: 'A full-service general ledger with double-entry checks, drillable transactions, and AI-driven reconciliation.',
    items: [
      { t: 'Chart of accounts', d: 'Prebuilt COA for 15+ industries — or bring your own.' },
      { t: 'Journal entries', d: 'Debit / credit with double-entry checks & recurring templates.' },
      { t: 'General ledger', d: 'Every posting drills to its source document in one click.' },
      { t: 'Bank reconciliation', d: 'AI matches transactions to statements — 100% coverage.' },
      { t: 'Recurring entries', d: 'Rent, subscriptions & payroll auto-post on schedule.' },
      { t: 'Statement importer', d: 'Drop a PDF — AI turns it into clean entries.' },
      { t: 'Batch import & CSV', d: 'Bring in years of history in a single upload.' },
      { t: 'Full audit log', d: 'Every change stamped with user, model & diff — reversible.' },
    ],
  },
  {
    id: 'invoicing', Icon: CreditCard, accent: 'violet',
    title: 'Invoicing & payments',
    tag: 'Stripe · ACH · Apple Pay · recurring',
    blurb: 'Branded invoices your customers can pay in one tap. Bill pay, recurring billing and public pay pages included.',
    items: [
      { t: 'Branded invoices', d: 'Send in your colors, your logo, your voice.' },
      { t: 'Bills & bill pay', d: 'Vendor bills, approval flows and scheduled payouts.' },
      { t: 'Recurring invoices', d: 'Set it once — CountBucks sends it monthly.' },
      { t: 'Stripe · ACH · Apple Pay', d: 'Every payment method your customers already use.' },
      { t: 'Public pay pages', d: 'A URL customers can pay without a login.' },
      { t: 'Auto-reminders', d: 'Chaser Buck drafts friendly follow-ups on schedule.' },
      { t: 'Payment plans', d: 'Split any invoice into installments in two clicks.' },
      { t: 'Customer statements', d: 'One-click statement PDFs for any date range.' },
    ],
  },
  {
    id: 'documents', Icon: PenTool, accent: 'blue',
    title: 'Documents & e-signatures',
    tag: 'Hub · OCR · e-sign · portal',
    blurb: 'Every receipt, invoice, statement and contract in one searchable, AI-organized hub — with e-signatures built in.',
    items: [
      { t: 'Document Hub & folders', d: 'Smart folders auto-organize what you drop in.' },
      { t: 'Email-forward inbox', d: 'Forward any receipt — AI files it for you.' },
      { t: 'AI OCR & extraction', d: 'Text, tables, totals, dates — extracted instantly.' },
      { t: 'E-signatures & envelopes', d: 'DocuSign-quality signing — no extra subscription.' },
      { t: 'Folder automations', d: 'Rules that move, rename or tag docs as they arrive.' },
      { t: 'Templates library', d: 'Contracts, W-9s and engagement letters ready to send.' },
      { t: 'Public folder shares', d: 'Give clients a magic-link folder — no login needed.' },
      { t: 'Client portal delivery', d: 'Send secure docs straight to your client\'s portal.' },
    ],
  },
  {
    id: 'tax', Icon: Calculator, accent: 'brand',
    title: 'Tax & reports',
    tag: 'P&L · balance sheet · Schedule C',
    blurb: 'Real-time financial statements, AI-driven tax-savings, and lender-ready packages — export or share with one link.',
    items: [
      { t: 'Real-time P&L', d: 'Numbers update the instant a transaction posts.' },
      { t: 'Balance sheet & cash flow', d: 'Full financial statements on any date range.' },
      { t: 'Tax Mapper (Schedule C)', d: 'Every category mapped to the right tax line.' },
      { t: 'AI tax-savings finder', d: 'Hunts for missed deductions year-round.' },
      { t: 'Public tax-savings reports', d: 'A shareable URL that shows what you saved.' },
      { t: 'DSCR & lender packages', d: 'One-click PDF for banks & underwriters.' },
      { t: 'Custom report builder', d: 'Group by class, project, department or tag.' },
      { t: 'Year-end close checklist', d: 'A guided close — nothing missed.' },
    ],
  },
  {
    id: 'ai', Icon: Bot, accent: 'violet',
    title: 'AI agents & assistants',
    tag: 'AI Buck · CFO Buck · Reconcile · Memory',
    blurb: 'A team of specialist AI agents that reads, categorizes, reconciles and answers — with every answer traceable to your data.',
    items: [
      { t: 'AI Buck bookkeeper', d: 'Reads, categorizes and files every document 24/7.' },
      { t: 'CFO Buck analyst', d: 'Answers questions about your numbers in plain English.' },
      { t: 'Reconcile Buck', d: 'Matches bank feeds to books automatically.' },
      { t: 'AI Command Center', d: 'One panel to see everything AI is doing right now.' },
      { t: 'AI Documents Q&A', d: 'Ask questions across every doc you\'ve ever uploaded.' },
      { t: 'Memory Hub context', d: 'CountBucks remembers your business, not just your prompt.' },
      { t: 'Daily Brief email', d: 'Tomorrow\'s cash position + one action to take.' },
      { t: 'Hallucination safeguards', d: 'Grounded citations & human-approve mode built in.' },
    ],
  },
  {
    id: 'team', Icon: Users, accent: 'blue',
    title: 'Team & client ops',
    tag: 'CRM-lite · time · tasks · portal',
    blurb: 'Everything you\'d normally patch together with Trello, Harvest, HubSpot and Intercom — natively wired to your books.',
    items: [
      { t: 'Contacts & CRM-lite', d: 'Customers, vendors, leads — with notes & files.' },
      { t: 'Time tracking & billable hours', d: 'Timer, timesheets & auto-invoicing.' },
      { t: 'Task manager & checklists', d: 'Assign work, due dates and recurring tasks.' },
      { t: 'Team roles & permissions', d: 'Fine-grained access with impersonation for support.' },
      { t: 'Client portal & messages', d: 'Secure two-way messaging & document sharing.' },
      { t: 'Guided tours', d: 'In-app walkthroughs that teach the platform as they work.' },
      { t: 'Support console & tickets', d: 'A unified inbox for tickets, replies & client errors.' },
      { t: 'University (training)', d: 'A library of short videos & courses — for you and staff.' },
    ],
  },
];

const ACCENT_MAP = {
  brand:  { text: 'text-brand-300',  border: 'border-brand-500/40', bg: 'bg-brand-500/[0.06]',  glow: 'from-brand-500/20',  dot: 'bg-brand-400' },
  violet: { text: 'text-violet-300', border: 'border-violet-500/40', bg: 'bg-violet-500/[0.06]', glow: 'from-violet-500/20', dot: 'bg-violet-400' },
  blue:   { text: 'text-blue-300',   border: 'border-blue-500/40',   bg: 'bg-blue-500/[0.06]',   glow: 'from-blue-500/20',   dot: 'bg-blue-400' },
};

const TIERS = [
  { id: 'starter', name: 'Starter', price: 19.99, tagline: 'DIY — stay organized',
    features: ['Single user (owner only)', '200 AI credits/month', '2 GB storage', '1 bank account · Plaid sync', 'OCR + AI auto-categorize', 'Invoicing & basic reports'] },
  { id: 'pro', name: 'Pro', price: 49.99, popular: true, tagline: 'Most popular — more power + AI',
    features: ['Everything in Starter', '5 users · multi-workspace', '1,500 AI credits/month', '25 GB storage · 4 banks', 'Daily Brief · e-signatures · Stripe · Time tracking', 'Client portal'] },
  { id: 'platinum', name: 'Platinum', price: 99.99, tagline: 'Advisor call included',
    features: ['Everything in Pro', '15-min monthly strategy call with a business advisor', 'Unlimited users', '4,000 AI credits/month', '100 GB storage · 10 banks', 'Gmail · Calendar · API access'] },
  { id: 'done_for_you', name: 'Done For You', price: 299.99, tagline: 'Tax return + bookkeeping included',
    features: ['Everything in Platinum', 'Annual tax return prep & filing INCLUDED', 'Dedicated CPA / EA does the books monthly', '15-min monthly advisor strategy call', '150 GB storage · 15 banks', 'Priority support · white-glove QuickBooks migration'] },
];

// Recurring add-ons surfaced under the pricing grid.
const ADDONS = [
  { id: 'payroll', label: 'Payroll Add-on', price: 199,
    blurb: 'Flat-rate payroll for any plan. Unlimited employees, tax filings, direct deposit.' },
  { id: 'tax_return', label: 'Tax Return Add-on', price: 49,
    blurb: 'A CountBucks CPA prepares & files your annual federal + state return.' },
  { id: 'storage_50', label: '+50 GB storage', price: 4,
    blurb: 'For solo brands collecting receipts.' },
  { id: 'storage_250', label: '+250 GB storage', price: 10,
    blurb: 'For growing businesses with PDFs & contracts.' },
  { id: 'storage_1tb', label: '+1 TB storage', price: 24,
    blurb: 'For agencies, firms & full archives.' },
  { id: 'storage_5tb', label: '+5 TB storage', price: 99,
    blurb: 'For high-volume teams archiving years of records.' },
];


// ─── Section primitives ─────────────────────────────────────────────────────

function Overline({ children, className }) {
  return (
    <p className={cn('text-[11px] font-mono uppercase tracking-[0.22em] text-zinc-500', className)}>
      {children}
    </p>
  );
}

function SectionHeading({ overline, title, sub }) {
  return (
    <div className="text-center max-w-3xl mx-auto mb-16">
      {overline && <Overline className="mb-4">{overline}</Overline>}
      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tighter leading-tight mb-4">
        {title}
      </h2>
      {sub && <p className="text-zinc-400 text-lg leading-relaxed">{sub}</p>}
    </div>
  );
}

function GradientMesh() {
  return (
    <div className="pointer-events-none fixed inset-0 overflow-hidden z-0">
      <div className="absolute top-[-10%] left-[-10%] w-[600px] h-[600px] rounded-full bg-brand-500/10 blur-[120px]" />
      <div className="absolute top-[20%] right-[-20%] w-[700px] h-[700px] rounded-full bg-violet-500/10 blur-[140px]" />
      <div className="absolute bottom-[-20%] left-[20%] w-[800px] h-[800px] rounded-full bg-blue-500/8 blur-[160px]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.04)_1px,transparent_0)] [background-size:32px_32px]" />
    </div>
  );
}


// ─── Top-level page ─────────────────────────────────────────────────────────

export default function LandingPageDark() {
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  // Pricing-toggle: 'monthly' (default) | 'annual' (15% discount).
  const [billing, setBilling] = useState('monthly');
  // Firm-menu: which category detail-panel is expanded (null = collapsed).
  const [openCategory, setOpenCategory] = useState(null);

  useEffect(() => {
    (async () => {
      const u = await checkAuth();
      if (u) navigate('/dashboard', { replace: true });
    })();
  }, [checkAuth, navigate]);

  return (
    <div className="relative min-h-screen bg-black text-zinc-100 overflow-x-hidden" data-testid="landing-page-dark">
      <GradientMesh />

      {/* Nav (shared component — same on / and /features) */}
      <LandingNav />

      <main id="top" className="relative z-10">
        {/* ─── HERO ────────────────────────────────────────────── */}
        <section className="px-6 lg:px-8 pt-24 pb-32" data-testid="hero">
          <div className="max-w-7xl mx-auto text-center">
            <Overline>THE ALL-IN-ONE BUSINESS HUB · POWERED BY EVERY AI</Overline>
            <h1 className="mt-6 text-5xl sm:text-6xl lg:text-7xl font-bold text-white tracking-tighter leading-[1.05]">
              Replace your entire stack.<br />
              <span className="bg-gradient-to-r from-brand-300 via-violet-300 to-blue-300 bg-clip-text text-transparent">
                Powered by the world's best AI.
              </span>
            </h1>
            {/* Trust line — answers the unspoken "but is there a real human if I get
                stuck?" question. Sits between the gradient headline and the
                marketing subhead so it's the first thing the eye lands on
                after the hook. */}
            <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-emerald-500/30 bg-emerald-500/[0.06] backdrop-blur-sm"
                 data-testid="hero-trust-line">
              <span className="relative flex items-center justify-center w-5 h-5">
                <span className="absolute inset-0 rounded-full bg-emerald-500/40 animate-ping" aria-hidden />
                <Users className="relative w-3.5 h-3.5 text-emerald-300" aria-hidden />
              </span>
              <span className="text-sm sm:text-[15px] text-emerald-100/90 font-medium leading-tight">
                Built &amp; backed by real <span className="text-emerald-300 font-semibold">CPAs and accountants</span> — here when you need a human.
              </span>
            </div>
            <p className="mt-7 text-lg sm:text-xl text-zinc-400 max-w-2xl mx-auto leading-relaxed">
              Books, banking, docs, time, invoices, client portal — and every premium AI model in one chat box.{' '}
              <span className="text-zinc-200">Starting at $19.99/mo.</span>
            </p>
            <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/login?mode=register&plan=starter&cadence=monthly" data-testid="hero-cta-primary">
                <Button size="lg" className="bg-brand-500 hover:bg-brand-400 text-black font-semibold gap-2 px-6 h-12 text-base">
                  Choose Starter — $19.99/mo <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <a href="#pricing" data-testid="hero-cta-secondary">
                <Button size="lg" variant="outline" className="border-white/15 text-zinc-200 hover:bg-white/5 hover:text-white h-12 px-6">
                  See pricing
                </Button>
              </a>
            </div>
            <p className="mt-5 text-xs text-zinc-500 font-mono uppercase tracking-wider">
              Card required · Free for 7 days · Cancel anytime · No long-term contract
            </p>


            {/* Live demo */}
            <div className="mt-20">
              <TypewriterDemo />
            </div>
          </div>
        </section>

        {/* ─── TRUST STRIP ─────────────────────────────────────── */}
        <section className="px-6 lg:px-8 py-14 border-y border-white/5 bg-zinc-950/40 backdrop-blur" data-testid="trust-strip">
          <div className="max-w-6xl mx-auto">
            <Overline className="text-center mb-8">The AI labs you already trust</Overline>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-x-6 gap-y-8">
              {TRUST_LOGOS.map(({ name, sub, Mark }) => (
                <div
                  key={name}
                  className="text-center group flex flex-col items-center gap-2.5"
                  data-testid={`trust-logo-${name.toLowerCase()}`}
                >
                  <Mark className="w-9 h-9 opacity-90 group-hover:opacity-100 group-hover:scale-[1.06] transition-all duration-200" />
                  <p className="font-mono font-bold text-zinc-300 tracking-tight text-sm uppercase group-hover:text-white transition-colors">
                    {name}
                  </p>
                  <p className="text-[10px] text-zinc-600 -mt-1 font-mono">{sub}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── REPLACES RECEIPT ───────────────────────────────── */}
        <section id="replaces" className="px-6 lg:px-8 py-32" data-testid="replaces-section">
          <SectionHeading
            overline="ONE SUBSCRIPTION · ZERO COMPROMISES"
            title="One CountBucks replaces all of these."
            sub="Stop juggling 8 logins, 8 invoices, 8 places your data lives. We did the consolidation work so you don't have to."
          />

          <div className="max-w-3xl mx-auto">
            <div className="rounded-2xl border border-white/10 bg-zinc-950/80 backdrop-blur-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
                <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">Your old stack</span>
                <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">Monthly</span>
              </div>
              <ul className="divide-y divide-white/5">
                {REPLACED_SUBS.map((sub) => (
                  <li key={sub.name} className="px-6 py-3.5 flex items-center justify-between group" data-testid={`replace-${sub.name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <X className="w-3.5 h-3.5 text-red-500/60 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-zinc-200 font-medium text-sm truncate line-through decoration-zinc-600">{sub.name}</p>
                        <p className="text-[11px] text-zinc-500 font-mono">{sub.category}</p>
                      </div>
                    </div>
                    <span className="font-mono text-zinc-400 line-through decoration-zinc-600 text-sm">${sub.cost}</span>
                  </li>
                ))}
              </ul>
              <div className="px-6 py-4 border-t border-white/10 bg-black/40 flex items-center justify-between">
                <span className="text-zinc-300 font-semibold text-sm">Old total</span>
                <span className="font-mono text-zinc-400 line-through decoration-zinc-600 text-lg">${REPLACED_TOTAL}/mo</span>
              </div>
              <div className="px-6 py-5 border-t border-brand-500/30 bg-gradient-to-r from-brand-500/10 via-transparent to-violet-500/10 flex items-center justify-between" data-testid="replaces-total-row">
                <span className="text-white font-semibold flex items-center gap-2">
                  <Check className="w-4 h-4 text-brand-400" />
                  CountBucks Starter
                </span>
                <span className="font-mono text-brand-300 text-2xl font-bold">$19.99/mo</span>
              </div>
            </div>
            <p className="text-center text-sm text-zinc-500 mt-6 font-mono">
              <span className="text-brand-400">~${REPLACED_TOTAL - 19.99}</span> back in your pocket every month.
            </p>
          </div>
        </section>

        {/* ─── FEATURE BENTO ──────────────────────────────────── */}
        <section id="features" className="px-6 lg:px-8 py-32 border-t border-white/5" data-testid="features">
          <SectionHeading
            overline="THE 6 PILLARS"
            title="Everything your business needs. Nothing it doesn't."
          />

          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-4">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.title}
                  data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  className={cn(
                    'rounded-2xl border border-white/10 bg-zinc-950/60 backdrop-blur-xl p-7 group transition-all hover:border-brand-500/30 hover:bg-zinc-950/80',
                    f.span,
                  )}
                >
                  <div className="w-9 h-9 rounded-lg border border-white/10 bg-black/40 flex items-center justify-center mb-5 group-hover:border-brand-500/40 transition-colors">
                    <Icon className="w-4.5 h-4.5 text-brand-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white tracking-tight mb-1">{f.title}</h3>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-brand-400/80 mb-3">{f.tag}</p>
                  <p className={cn('text-zinc-400 leading-relaxed', f.wide ? 'text-base max-w-2xl' : 'text-sm')}>
                    {f.blurb}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        {/* ─── FIRM CAPABILITY MENU (expandable) ──────────────── */}
        <section id="firm-menu" className="px-6 lg:px-8 py-32 border-t border-white/5" data-testid="firm-menu-section">
          <SectionHeading
            overline="THE FULL CAPABILITY MENU · 48+ WORKFLOWS"
            title="Everything a real firm does. Under one roof."
            sub="CountBucks isn't a lightweight app with a bookkeeping bolt-on. It's the full stack of what a modern accounting firm handles — engineered into one platform, powered by AI. Tap any tile to expand."
          />

          <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="firm-menu">
            {FIRM_CATEGORIES.map((c) => {
              const a = ACCENT_MAP[c.accent];
              const isOpen = openCategory === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setOpenCategory(isOpen ? null : c.id)}
                  aria-expanded={isOpen}
                  aria-controls={`firm-menu-panel-${c.id}`}
                  data-testid={`firm-menu-card-${c.id}`}
                  className={cn(
                    'text-left rounded-2xl border backdrop-blur-xl p-6 group transition-all',
                    isOpen
                      ? `${a.border} ${a.bg} shadow-[0_0_0_1px_rgba(255,255,255,0.02),0_24px_60px_-24px_rgba(0,0,0,0.6)]`
                      : 'border-white/10 bg-zinc-950/60 hover:border-white/20 hover:bg-zinc-950/80',
                  )}
                >
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div className={cn(
                      'w-10 h-10 rounded-lg border flex items-center justify-center transition-colors',
                      isOpen ? `${a.border} ${a.bg}` : 'border-white/10 bg-black/40 group-hover:border-white/20',
                    )}>
                      <c.Icon className={cn('w-4.5 h-4.5', isOpen ? a.text : 'text-zinc-300')} />
                    </div>
                    <ChevronDown className={cn(
                      'w-4 h-4 flex-shrink-0 transition-transform text-zinc-500 mt-1',
                      isOpen && 'rotate-180 text-zinc-300',
                    )} />
                  </div>
                  <h3 className="text-lg font-bold text-white tracking-tight mb-1">{c.title}</h3>
                  <p className={cn('font-mono text-[10px] uppercase tracking-widest mb-3', a.text)}>{c.tag}</p>
                  <p className="text-sm text-zinc-400 leading-relaxed">{c.blurb}</p>
                </button>
              );
            })}
          </div>

          {/* Expandable detail panel (single, lightweight) */}
          {openCategory && (() => {
            const c = FIRM_CATEGORIES.find((x) => x.id === openCategory);
            const a = ACCENT_MAP[c.accent];
            return (
              <div
                id={`firm-menu-panel-${c.id}`}
                data-testid={`firm-menu-panel-${c.id}`}
                className="max-w-6xl mx-auto mt-6 rounded-2xl border border-white/10 bg-zinc-950/80 backdrop-blur-xl overflow-hidden relative"
              >
                <div className={cn('absolute inset-x-0 -top-24 h-48 bg-gradient-to-b to-transparent blur-2xl opacity-70 pointer-events-none', a.glow)} />
                <div className="relative p-6 sm:p-8">
                  <div className="flex items-start justify-between gap-4 mb-6">
                    <div className="flex items-start gap-4">
                      <div className={cn('w-12 h-12 rounded-xl border flex items-center justify-center flex-shrink-0', a.border, a.bg)}>
                        <c.Icon className={cn('w-5 h-5', a.text)} />
                      </div>
                      <div>
                        <p className={cn('font-mono text-[10px] uppercase tracking-widest mb-1.5', a.text)}>{c.tag}</p>
                        <h4 className="text-2xl font-bold text-white tracking-tight">{c.title}</h4>
                        <p className="text-zinc-400 leading-relaxed mt-2 max-w-2xl">{c.blurb}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setOpenCategory(null)}
                      aria-label="Close"
                      data-testid={`firm-menu-close-${c.id}`}
                      className="w-8 h-8 rounded-lg border border-white/10 bg-black/40 hover:bg-white/5 hover:border-white/20 flex items-center justify-center flex-shrink-0 transition-colors"
                    >
                      <X className="w-3.5 h-3.5 text-zinc-400" />
                    </button>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
                    {c.items.map((it) => (
                      <div key={it.t} className="flex items-start gap-3">
                        <span className={cn('mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0', a.dot)} />
                        <div>
                          <p className="text-zinc-100 font-semibold text-sm tracking-tight">{it.t}</p>
                          <p className="text-zinc-500 text-sm leading-relaxed mt-0.5">{it.d}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 pt-6 border-t border-white/5 flex flex-wrap items-center justify-between gap-4">
                    <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 flex items-center gap-2">
                      <Check className={cn('w-3.5 h-3.5', a.text)} />
                      Included in every plan — no per-feature upsells
                    </p>
                    <Link
                      to="/login?mode=register&plan=starter&cadence=monthly"
                      data-testid={`firm-menu-cta-${c.id}`}
                    >
                      <Button size="sm" className="bg-brand-500 hover:bg-brand-400 text-black font-semibold gap-2">
                        Try {c.title} <ArrowRight className="w-3.5 h-3.5" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            );
          })()}

          {!openCategory && (
            <p className="text-center text-xs font-mono uppercase tracking-widest text-zinc-600 mt-8">
              Tap any tile above to expand · 48+ workflows · zero add-ons
            </p>
          )}
        </section>

        {/* ─── AI HUB TEASER ──────────────────────────────────── */}
        <section id="ai-hub" className="px-6 lg:px-8 py-32 border-t border-white/5 relative" data-testid="ai-hub-teaser">
          <div className="absolute inset-0 bg-gradient-to-b from-violet-500/5 via-transparent to-brand-500/5 pointer-events-none" />
          <div className="relative max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Overline>THE AI HUB · COMING THIS WEEK</Overline>
              <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tighter leading-tight">
                One chat box.<br />
                <span className="bg-gradient-to-r from-violet-300 to-brand-300 bg-clip-text text-transparent">
                  Every AI on Earth.
                </span>
              </h2>
              <p className="mt-6 text-zinc-400 text-lg leading-relaxed">
                Don't pay $20/mo for ChatGPT, another $20 for Claude, another $20 for Gemini, another $20 for Perplexity.
                Pick the right model for every question — or let CountBucks pick for you.
              </p>
              <div className="mt-8 space-y-3">
                {[
                  { icon: Sparkles, t: '240+ models in one dropdown', s: 'Claude, GPT-4o, Gemini, Perplexity, Llama, DeepSeek, Mistral, Qwen, Grok…' },
                  { icon: ShieldCheck, t: 'Auto-fallback to free models', s: 'Hit your premium cap? We seamlessly switch to Llama 3.3. You never get blocked.' },
                  { icon: Zap, t: 'Web search + vision + files', s: 'Upload PDFs, drop in images, ask the web — all in the same chat.' },
                ].map((row) => {
                  const Icon = row.icon;
                  return (
                    <div key={row.t} className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-md border border-white/10 bg-black/40 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-3.5 h-3.5 text-violet-300" />
                      </div>
                      <div>
                        <p className="text-zinc-100 font-semibold text-sm">{row.t}</p>
                        <p className="text-zinc-500 text-sm leading-relaxed">{row.s}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Mock chat UI */}
            <div className="relative">
              <div className="absolute -inset-6 bg-gradient-to-br from-violet-500/20 to-brand-500/15 blur-3xl opacity-60" />
              <div className="relative rounded-2xl border border-white/10 bg-zinc-950/95 overflow-hidden shadow-[0_24px_120px_-32px_rgba(139,92,246,0.4)]">
                <div className="flex items-center gap-2 px-3 py-2 border-b border-white/5 bg-black/50">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">Model:</span>
                  <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-md border border-violet-500/40 bg-violet-500/10 text-violet-300 text-[11px] font-mono font-semibold">
                    <Sparkles className="w-2.5 h-2.5" /> Claude 3.5 Sonnet
                  </div>
                  <span className="text-zinc-700 text-[10px] font-mono">·</span>
                  <span className="text-zinc-500 text-[10px] font-mono">240+ more</span>
                </div>
                <div className="p-5 space-y-3 min-h-[200px]">
                  <div className="rounded-lg bg-zinc-900/50 border border-white/5 px-3 py-2 text-sm text-zinc-300">
                    Find me 5 deductible expense categories I'm probably missing.
                  </div>
                  <div className="rounded-lg bg-brand-500/5 border border-brand-500/20 px-3 py-2 text-sm text-zinc-200 leading-relaxed">
                    Based on your last 6 months of transactions, here are five categories you're under-tracking…
                  </div>
                </div>
                <div className="px-3 py-2 border-t border-white/5 flex items-center justify-between text-[10px] font-mono text-zinc-600">
                  <span>0.42¢ this question</span>
                  <span className="text-brand-400 flex items-center gap-1">
                    <InfinityIcon className="w-2.5 h-2.5" /> Unlimited Llama for free users
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ─── AUDIENCES ──────────────────────────────────────── */}
        <section className="px-6 lg:px-8 py-32 border-t border-white/5" data-testid="audiences">
          <SectionHeading
            overline="BUILT FOR YOUR WORKFLOW"
            title="From solo to firm. Same tool."
          />
          <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {AUDIENCES.map((a) => {
              const Icon = a.icon;
              return (
                <div
                  key={a.title}
                  data-testid={`audience-${a.title.toLowerCase().replace(/\s+/g, '-')}`}
                  className="rounded-xl border border-white/5 bg-zinc-950/60 p-6 transition-all hover:-translate-y-1 hover:border-white/15 hover:bg-zinc-950/90"
                >
                  <Icon className="w-5 h-5 text-zinc-400 mb-3" />
                  <h3 className="text-white font-bold tracking-tight">{a.title}</h3>
                  <p className="text-zinc-500 text-sm mt-1.5 leading-relaxed">{a.blurb}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* ─── LIVE CPA ──────────────────────────────────────── */}
        <section id="live-cpa" className="px-6 lg:px-8 py-32 border-t border-white/5 relative" data-testid="live-cpa">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-7">
                <Overline>NEW · LIVE CPA ACCESS</Overline>
                <h2 className="mt-4 text-4xl sm:text-5xl font-bold text-white tracking-tighter leading-[1.05]">
                  A real CPA.<br />
                  <span className="bg-gradient-to-r from-emerald-300 via-teal-300 to-cyan-300 bg-clip-text text-transparent">
                    Already inside your books.
                  </span>
                </h2>
                <p className="mt-6 text-lg text-zinc-400 leading-relaxed max-w-xl">
                  When you upgrade to <span className="text-white font-semibold">Platinum CFO</span>, a real CountBucks-vetted CPA logs into your live workspace, reviews your numbers, and answers your questions on a monthly video call. No emailing PDFs back and forth. No re-explaining your business every quarter.
                </p>
                <ul className="mt-7 space-y-3 text-zinc-300">
                  <li className="flex gap-3"><Check className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" /><span><strong className="text-white">30-minute monthly video call</strong> with your assigned CPA, looking at your actual books — not a sales call.</span></li>
                  <li className="flex gap-3"><Check className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" /><span><strong className="text-white">Year-end tax return prep &amp; filing INCLUDED</strong> — your CPA already knows your numbers, so they file faster and at no extra cost on Platinum.</span></li>
                  <li className="flex gap-3"><Check className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" /><span><strong className="text-white">Onboarding walkthrough</strong> — your CPA helps clean up the books in your first 30 days.</span></li>
                  <li className="flex gap-3"><Check className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" /><span><strong className="text-white">In-app Q&amp;A</strong> between calls — message your CPA any time and they reply within 1 business day.</span></li>
                </ul>
                <div className="mt-9 flex flex-col sm:flex-row gap-3">
                  <a href="#pricing" data-testid="live-cpa-cta">
                    <Button size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold gap-2 px-6 h-12 text-base">
                      See Platinum CFO pricing <ArrowRight className="w-4 h-4" />
                    </Button>
                  </a>
                </div>
              </div>
              <div className="lg:col-span-5">
                <div className="relative rounded-2xl border border-white/10 bg-gradient-to-br from-emerald-500/5 via-teal-500/5 to-cyan-500/5 p-8 overflow-hidden">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(16,185,129,0.18),transparent_50%)] pointer-events-none" />
                  <div className="relative">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-black font-bold text-lg">CB</div>
                      <div>
                        <p className="text-white font-semibold">Your CountBucks CPA</p>
                        <p className="text-xs text-emerald-300 font-mono uppercase tracking-wider">Verified · IRS Enrolled Agent</p>
                      </div>
                    </div>
                    <blockquote className="text-zinc-300 italic leading-relaxed border-l-2 border-emerald-400/40 pl-4">
                      "I looked at your Q3 books this morning. You're under-collecting on the Acme contract — want to walk through the AR aging on our call Thursday? I can also walk you through the S-corp distribution math while we're at it."
                    </blockquote>
                    <div className="mt-6 pt-5 border-t border-white/5 flex items-center justify-between text-xs font-mono">
                      <span className="text-emerald-300 uppercase tracking-wider">Today · 9:42 AM</span>
                      <span className="text-zinc-500">replied in 47 min</span>
                    </div>
                  </div>
                </div>
                <p className="mt-4 text-xs text-zinc-500 text-center font-mono uppercase tracking-wider">
                  Real conversation pattern — not a chatbot.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ─── PRICING ────────────────────────────────────────── */}
        <section id="pricing" className="px-6 lg:px-8 py-32 border-t border-white/5" data-testid="pricing">
          <SectionHeading
            overline="PRICING · NO HIDDEN FEES"
            title="Five plans. No surprises."
            sub="AI usage is measured in CREDITS, not dollars. Hit your monthly cap? Free Llama 3.3 keeps you unblocked."
          />

          {/* Billing toggle — Monthly / Annual (15% off). */}
          <div className="flex justify-center mb-12" data-testid="billing-toggle-wrap">
            <div className="relative inline-flex p-1 rounded-full border border-white/10 bg-zinc-950/60 backdrop-blur-md">
              {/* Sliding pill background. Two fixed-width buttons; pill animates between them. */}
              <span
                aria-hidden
                className={cn(
                  'absolute top-1 bottom-1 w-[120px] rounded-full bg-brand-400 transition-transform duration-300 ease-out',
                  billing === 'monthly' ? 'translate-x-0' : 'translate-x-[120px]',
                )}
                style={{ left: 4 }}
              />
              <button
                type="button"
                onClick={() => setBilling('monthly')}
                aria-pressed={billing === 'monthly'}
                data-testid="billing-toggle-monthly"
                className={cn(
                  'relative z-10 w-[120px] py-2 text-sm font-semibold rounded-full transition-colors',
                  billing === 'monthly' ? 'text-black' : 'text-zinc-400 hover:text-white',
                )}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setBilling('annual')}
                aria-pressed={billing === 'annual'}
                data-testid="billing-toggle-annual"
                className={cn(
                  'relative z-10 w-[120px] py-2 text-sm font-semibold rounded-full transition-colors flex items-center justify-center gap-1.5',
                  billing === 'annual' ? 'text-black' : 'text-zinc-400 hover:text-white',
                )}
              >
                Annual
                <span className={cn(
                  'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider',
                  billing === 'annual' ? 'bg-black/20 text-black' : 'bg-brand-500/15 text-brand-300',
                )}>−15%</span>
              </button>
            </div>
          </div>

          <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 justify-center">
            {TIERS.map((t) => {
              // Annual = monthly × 12 × 0.85, displayed as the effective monthly rate.
              const annualMonthly = t.price === 0 ? 0 : +(t.price * 0.85).toFixed(2);
              const displayPrice = billing === 'annual' ? annualMonthly : t.price;
              const annualTotal = +(t.price * 12 * 0.85).toFixed(0);
              return (
              <div
                key={t.id}
                data-testid={`pricing-tier-${t.id}`}
                className={cn(
                  'relative rounded-2xl border bg-zinc-950/60 backdrop-blur-xl p-6 flex flex-col transition-all',
                  t.popular
                    ? 'border-brand-500/60 ring-1 ring-brand-500/30 shadow-[0_0_60px_-12px_rgba(16,185,129,0.4)]'
                    : 'border-white/10 hover:border-white/20',
                )}
              >
                {t.popular && (
                  <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full bg-brand-500 text-black text-[10px] font-bold uppercase tracking-widest font-mono">
                    Most popular
                  </div>
                )}
                {t.id === 'free' && (
                  <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full bg-violet-500/20 border border-violet-500/40 text-violet-300 text-[10px] font-bold uppercase tracking-widest font-mono">
                    Forever
                  </div>
                )}
                <h3 className="font-bold text-white text-base">{t.name}</h3>
                <p className="text-[11px] font-mono text-zinc-500 uppercase tracking-widest mt-0.5">{t.tagline}</p>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className={cn(
                    'font-bold text-white tracking-tighter',
                    displayPrice >= 100 ? 'text-2xl sm:text-3xl' : 'text-3xl',
                  )} data-testid={`pricing-amount-${t.id}`}>${displayPrice}</span>
                  <span className="text-zinc-500 text-sm">/mo</span>
                </div>
                {/* Annual footnote — fixed height so monthly vs annual don't change card height */}
                <div className="h-5 mt-1">
                  {billing === 'annual' && t.price > 0 && (
                    <p className="text-[11px] font-mono text-brand-300/80 uppercase tracking-wider" data-testid={`pricing-annual-total-${t.id}`}>
                      ${annualTotal}/yr · save ${(t.price * 12 - annualTotal).toFixed(0)}
                    </p>
                  )}
                </div>
                <ul className="mt-5 space-y-2 flex-1">
                  {t.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-zinc-300">
                      <Check className="w-3 h-3 text-brand-400 mt-0.5 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/login?mode=register&plan=${t.id}&cadence=${billing}`}
                  data-testid={`pricing-cta-${t.id}`}
                  className="mt-6"
                  onClick={() => {
                    // Track the click for funnel analytics so we can measure
                    // landing → register → checkout conversion per-tier.
                    trackFunnel('landing.pricing_click', {
                      plan_id: t.id,
                      cadence: billing,
                      came_from: 'landing.pricing',
                    });
                    // Stash plan intent so the post-register flow can auto-trigger Stripe checkout
                    // with the right plan + cadence picked here.
                    if (t.id !== 'free') {
                      try {
                        window.localStorage.setItem('plan_intent', JSON.stringify({
                          plan_id: t.id, cadence: billing, ts: Date.now(),
                        }));
                      } catch { /* private mode — silent fail */ }
                    }
                  }}
                >
                  <Button
                    size="sm"
                    className={cn(
                      'w-full font-semibold',
                      t.popular
                        ? 'bg-brand-500 hover:bg-brand-400 text-black'
                        : 'bg-white/5 hover:bg-white/10 text-white border border-white/10',
                    )}
                  >
                    {'Choose ' + t.name}
                  </Button>
                </Link>
              </div>
              );
            })}
          </div>

          {/* Add-ons (stack with any paid tier) */}
          <div className="max-w-5xl mx-auto mt-12 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="pricing-addons">
            {ADDONS.map((a) => (
              <div
                key={a.id}
                className="rounded-xl border border-white/10 bg-zinc-950/40 backdrop-blur-sm p-4 flex items-center justify-between gap-3 hover:border-white/20 transition-colors"
                data-testid={`pricing-addon-${a.id}`}
              >
                <div className="min-w-0">
                  <div className="text-white font-semibold text-sm">{a.label}</div>
                  <div className="text-zinc-500 text-xs mt-0.5 truncate">{a.blurb}</div>
                </div>
                <div className="shrink-0 text-right">
                  <span className="text-brand-400 font-bold text-sm">${a.price}</span>
                  <span className="text-zinc-500 text-xs">/mo</span>
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-[11px] font-mono uppercase tracking-widest text-zinc-600 mt-6">
            Annual billing saves 15% on all paid tiers
          </p>
          <p className="text-center text-xs text-zinc-500 mt-2 max-w-xl mx-auto">
            All plans include a <span className="text-zinc-300 font-semibold">7-day free trial</span>.
            Card required at signup — your card is charged on day 8 unless
            you cancel from Settings → Billing.
          </p>
        </section>

        {/* ─── FOOTER CTA ─────────────────────────────────────── */}
        <section className="px-6 lg:px-8 py-32 border-t border-white/5 relative overflow-hidden" data-testid="footer-cta">
          <div className="absolute inset-0 bg-gradient-radial from-brand-500/15 via-transparent to-transparent pointer-events-none"
               style={{ background: 'radial-gradient(ellipse at center, rgba(16,185,129,0.12), transparent 65%)' }} />
          <div className="relative max-w-3xl mx-auto text-center">
            <h2 className="text-4xl sm:text-5xl font-bold text-white tracking-tighter leading-tight">
              Stop renting 8 SaaS subscriptions.<br />
              <span className="bg-gradient-to-r from-brand-300 to-violet-300 bg-clip-text text-transparent">
                Own your business OS.
              </span>
            </h2>
            <p className="mt-5 text-zinc-400 text-lg">
              Starter is $19.99/mo · Card required, free for 7 days · 30 seconds to set up.
            </p>
            <div className="mt-8">
              <Link to="/login?mode=register&plan=starter&cadence=monthly" data-testid="footer-cta-btn">
                <Button size="lg" className="bg-brand-500 hover:bg-brand-400 text-black font-semibold gap-2 px-8 h-14 text-base">
                  Choose Starter — $19.99/mo <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* ─── FOOTER ─────────────────────────────────────────── */}
        <footer className="border-t border-white/5 bg-black/60 backdrop-blur">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <img src="/countbucks-icon.png" alt="CountBucks" className="w-7 h-7" />
              <span className="text-zinc-300 font-bold text-sm tracking-tight">CountBucks</span>
              <span className="text-zinc-600 text-xs ml-2">© {new Date().getFullYear()}</span>
            </div>
            <div className="flex items-center gap-5 text-xs text-zinc-500 font-mono">
              <a href="#features" className="hover:text-zinc-300 transition-colors">Features</a>
              <a href="#pricing" className="hover:text-zinc-300 transition-colors">Pricing</a>
              <a href="#live-cpa" className="hover:text-zinc-300 transition-colors" data-testid="footer-live-cpa-link">Live CPA</a>
              <Link to="/login" className="hover:text-zinc-300 transition-colors">Sign in</Link>
              <Link to="/privacy" className="hover:text-zinc-300 transition-colors" data-testid="footer-privacy-link">Privacy</Link>
              <Link to="/terms" className="hover:text-zinc-300 transition-colors" data-testid="footer-terms-link">Terms</Link>
            </div>
          </div>
        </footer>
      </main>

      <BackToTop />
    </div>
  );
}
